/**
 * Fetch wrapper with automatic 401 handling and token refresh
 *
 * This module provides a fetch wrapper that:
 * 1. Makes requests with the current JWT token
 * 2. Intercepts 401 responses
 * 3. Automatically refreshes the token
 * 4. Retries the original request with the new token
 * 5. Triggers logout if refresh fails
 *
 * Uses singleton pattern to prevent concurrent refresh attempts.
 */

import { getJwtToken, getAuthHeader, refreshToken } from './jwtService';

// Singleton promise to prevent concurrent refresh attempts
let refreshPromise = null;

// Logout callback - set by AuthContext
let logoutCallback = null;

/**
 * Register the logout callback from AuthContext
 * @param {Function} callback - Function to call when token refresh fails
 */
export function setLogoutCallback(callback) {
  logoutCallback = callback;
}

/**
 * Perform token refresh with singleton lock
 * Ensures only one refresh happens at a time
 * @returns {Promise<{success: boolean, token?: string}>}
 */
async function refreshWithLock() {
  if (refreshPromise) {
    // Another refresh is in progress, wait for it
    console.log('[fetchWithAuth] Waiting for existing refresh to complete');
    return refreshPromise;
  }

  console.log('[fetchWithAuth] Starting token refresh');
  refreshPromise = refreshToken();

  try {
    const result = await refreshPromise;
    return result;
  } finally {
    // Clear the lock after completion
    refreshPromise = null;
  }
}

/**
 * Handle logout when token refresh fails
 */
function handleLogout() {
  console.log('[fetchWithAuth] Token refresh failed, triggering logout');
  if (logoutCallback) {
    logoutCallback();
  } else {
    console.warn('[fetchWithAuth] No logout callback registered');
  }
}

/**
 * Merge auth header into existing headers
 * @param {object} existingHeaders - Existing headers object
 * @returns {object} - Headers with Authorization added
 */
function mergeAuthHeaders(existingHeaders = {}) {
  const authHeader = getAuthHeader();
  return {
    ...existingHeaders,
    ...authHeader
  };
}

/**
 * Fetch with automatic 401 handling and token refresh
 *
 * @param {string} url - The URL to fetch
 * @param {object} options - Fetch options (method, headers, body, etc.)
 * @param {object} config - Additional configuration
 * @param {boolean} config.skipAuth - Skip adding Authorization header (for anonymous endpoints)
 * @param {boolean} config.isRetry - Internal flag to prevent infinite retry loops
 * @returns {Promise<Response>} - The fetch Response object
 *
 * @example
 * // Basic usage
 * const response = await fetchWithAuth('/api/glossary/list', {
 *   method: 'GET',
 *   headers: { 'Content-Type': 'application/json' }
 * });
 *
 * @example
 * // Anonymous endpoint (skip auth)
 * const response = await fetchWithAuth('/api/session/status', {}, { skipAuth: true });
 */
export async function fetchWithAuth(url, options = {}, config = {}) {
  const { skipAuth = false, isRetry = false } = config;

  // Check if we have a token before making the request
  const token = getJwtToken();
  if (!token && !skipAuth) {
    console.warn('[fetchWithAuth] No JWT token available');
    // Trigger logout since we have no token
    handleLogout();
    throw new Error('No authentication token available');
  }

  // Prepare headers with auth
  const headers = skipAuth
    ? options.headers || {}
    : mergeAuthHeaders(options.headers);

  // Make the request
  const response = await fetch(url, {
    ...options,
    headers
  });

  // Check for 401 Unauthorized
  if (response.status === 401 && !skipAuth && !isRetry) {
    console.log('[fetchWithAuth] Received 401, attempting token refresh');

    // Try to refresh the token
    const refreshResult = await refreshWithLock();

    if (refreshResult.success && refreshResult.token) {
      console.log('[fetchWithAuth] Token refreshed, retrying request');

      // Retry the request with the new token
      const retryHeaders = {
        ...options.headers,
        'Authorization': `Bearer ${refreshResult.token}`
      };

      return fetch(url, {
        ...options,
        headers: retryHeaders
      });
    } else {
      // Refresh failed - trigger logout
      console.error('[fetchWithAuth] Token refresh failed:', refreshResult.error);
      handleLogout();
      throw new Error('Session expired. Please log in again.');
    }
  }

  return response;
}

/**
 * Convenience method for JSON fetch with auth
 * Automatically parses JSON response and handles common error cases
 *
 * @param {string} url - The URL to fetch
 * @param {object} options - Fetch options
 * @param {object} config - Additional configuration (skipAuth, etc.)
 * @returns {Promise<any>} - Parsed JSON response
 * @throws {Error} - If response is not ok or JSON parsing fails
 *
 * @example
 * const data = await fetchJsonWithAuth('/api/glossary/list?tenantId=123', {
 *   method: 'GET'
 * });
 */
export async function fetchJsonWithAuth(url, options = {}, config = {}) {
  const response = await fetchWithAuth(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers
    }
  }, config);

  if (!response.ok) {
    // Try to parse error response
    const errorData = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(errorData.error || `Request failed with status ${response.status}`);
  }

  return response.json();
}

export default fetchWithAuth;
